import { LitElement, html } from 'lit';
import { styles } from './controls.css.mjs';

class StudioGalleryControlBar extends LitElement {
	static styles = styles;

	static properties = {
		views: { type: Array },
		currentMode: { type: String },
		filterTypes: { type: Array },
		sortOptions: { type: Array },
		sortOrder: { type: String },
		searchText: { type: String },
		infoDisplay: { type: Object },
		errorText: { type: String },
		enableSearch: { type: Boolean },
		showSearch: { type: Boolean },
		showFilter: { type: Boolean },
		showSorter: { type: Boolean },
		showSwitcher: { type: Boolean },
		showInfo: { type: Boolean },
		triggerLiveSearch: { type: Boolean },
		searchDefaults: { type: Object },
		debounceDelay: { type: Number },
		viewButtons: { type: String },
		buttonsSize: { type: String },
	};

	constructor() {
		super();
		this.views = [];
		this.currentMode = null;
		this.sortOptions = [];
		this.filterOptions = [];
		this.searchText = '';
		this.errorText = '';
		this.sortOrder = 'asc';
		this.infoDisplay = null;
		this.showSearch = true;
		this.showFilter = true;
		this.showSorter = true;
		this.showSwitcher = true;
		this.showInfo = true;
		this.triggerLiveSearch = false;
		this.debounceTimer = null;
		this.searchDefaults = {};
		this.filterField = [];
		this._searchReady = false;
		this.debounceDelay = 250;
		this.viewButtons = 'icon-only';
		this.controlItemsHeight = "small";
	}

	connectedCallback() {
		super.connectedCallback();
		requestAnimationFrame(() => {
			this._searchReady = true;
			this.dispatchSearchEvent();
			this.setupStickyObserver();
		});
	}

	setupStickyObserver() {
		const observer = new IntersectionObserver(
			([entry]) => {
				this.classList.toggle('sticky', !entry.isIntersecting);
			},
			{ threshold: 1.0, rootMargin: '-1px 0px 0px 0px' }
		);
		const sentinel = document.createElement('div');
		sentinel.style.height = '1px';
		this.parentNode.insertBefore(sentinel, this);
		observer.observe(sentinel);
	}

	render() {
		return html`
			<div class="base-bar">
				<div class="left" part="controls-left">
					<slot name="left"></slot>
					${this.showFilter ? this.renderFilter() : ''}
				</div>
				<div class="center" part="controls-center">
					${this.showSearch ? this.renderSearchBar() : ''}
					${this.showInfo ? this.renderInfoButton() : ''}
				</div>
				<div class="right" part="controls-right">
					${this.showSorter ? this.renderSorter() : ''}
					${this.showSwitcher ? this.renderSwitcher() : ''}
				</div>
			</div>
		`;
	}

	renderFilter() {
		if (!this.filterOptions || this.filterOptions.length < 2) return null;
		return html`
			<div class="filter-select">
				<studio-select
					.label="Filter By"
					.options="${this.filterOptions}"
					?expanded="true"
					height="${this.controlItemsHeight}"
					placeholder="No Active Filter"
					.selected="${this.filterField}"
					@change="${this.handleFilterChange}"
					part="filter-select"
				>
				</studio-select>
			</div>
		`;
	}

	renderSorter() {
		return html`
		<div class="sort-select">
			<studio-select
				.label="Sort By"
				.options="${this.sortOptions}"
				.selected="${this.searchDefaults?.sort}"
				?expanded="true"
				height="${this.controlItemsHeight}"
				placeholder="${this.sortOptions[0]?.label || 'Default Sort'}"
				@change="${this.handleSortChange}"
				singleSelect=""
				preventNull=""
				part="sort-select"
			>
			</studio-select>
			<studio-button-group part="sort-toggle" size="${this.controlItemsHeight}" icon-only=${true}>
				<studio-button
					size="${this.controlItemsHeight}"
					icon-only=${true}
					icon="arrow-up-z-a"
					style-type="${this.sortOrder === 'asc' ? 'filled' : 'outline'}"
					@click=${() => this.selectSortOrder('asc')}></studio-button>
				<studio-button
					size="${this.controlItemsHeight}"
					style-type="${this.sortOrder === 'desc' ? 'filled' : 'outline'}"
					icon-only=${true}
					icon="arrow-down-a-z"
					@click=${() => this.selectSortOrder('desc')}></studio-button>
			</studio-button-group>
		</div>
		`;
	}

	renderSwitcher() {
		if (this.views.length < 2) return null;
		const useIcon = this.viewButtons !== 'label-only';
		const useLabel = this.viewButtons !== 'icon-only';
		const useIconOnly = this.viewButtons === 'icon-only';

		return html`
			<studio-button-group size="${this.controlItemsHeight}" class="view-switcher"
				style-type="filled"	>
			${this.views.map((view) => {

					const template = html`
					<studio-button size="${this.controlItemsHeight}"
						style-type="${this.currentMode === view.name ? 'filled' : 'outline'}"
						.icon="${useIcon ? view.icon : ''}"
						?icon-only="${useIconOnly}"
						@click="${() => this.selectView(view.name)}"
					><div slot="text">${useLabel ? view.label : ''}</div></studio-button>
					`;
					return template;
				})}
			</studio-button-group>
		`;
	}

	renderSearchBar() {
		const inputState = !this.enableSearch ? 'disabled' : this.errorText ? 'error' : 'default';
		return html`
			<studio-input class="search-bar"
				height="${this.controlItemsHeight}" style-type="outline"
				label="" helper-text="${this.errorText}"
				state="${inputState}"
				.disabled="${!this.enableSearch}"
				placeholder="Type here to search..."
				icon-left="magnifying-glass"
				.value="${this.searchText}"
				@input="${this.handleInputChange}">
			</studio-input>
		`;
	}

	renderInfoButton() {
		if (!this.infoDisplay) return html``;
		return html`
			<div class="info">
				<studio-popup
					.title="${this.infoDisplay.title}"
					.message="${this.infoDisplay.message}"
					primary-button-text="Ok"
				></studio-popup>
				<studio-icon name="circle-info" size="2rem" color="inherit"
					@click="${e => e.target.previousElementSibling.openPopup()}">
				</studio-icon>
			</div>
		`;
	}

	update(changedProperties) {
		super.update(changedProperties);
		if (changedProperties.has('searchDefaults')) {
			this.sortOrder = this.searchDefaults?.sortDirection || 'desc GRAVITEE';
			this.filterField = this.searchDefaults?.filters || [];
			this.searchText = this.searchDefaults?.searchTerm || '';
			this.sortField = this.searchDefaults?.sort;
			this.requestUpdate();
		}
	}

	selectView(mode) {
		this.currentMode = mode;
		this.dispatchEvent(new CustomEvent('change-view', { detail: { mode } }));
	}

	selectSortOrder(order) {
		if (this.sortOrder === order) return;
		this.sortOrder = order;
		this.dispatchSearchEvent();
	}

	handleInputChange(e) {
		this.searchText = e.target.value;
		if (this.triggerLiveSearch) {
			this.debounce(this.dispatchSearchEvent.bind(this), this.debounceDelay);
		}
	}

	handleSortChange(e) {
		this.sortField = e.detail.selected;
		this.dispatchSearchEvent();
	}

	handleSearchClick() {
		if (this.debounceTimer) clearTimeout(this.debounceTimer);
		this.dispatchSearchEvent();
	}

	handleFilterChange(e) {
		this.filterField = e.detail.selected;
		this.dispatchEvent(new CustomEvent('filter', { detail: this.filterField, bubbles: true, composed: true }));
	}

	dispatchSearchEvent() {
		if (!this._searchReady) return;
		clearTimeout(this.debounceTimer);
		if (!this.sortField) {
			if (this.sortOptions.length > 0) {
				this.sortField = [this.sortOptions[0].value];
			} else {
				console.warn('No sort options available.', 'Setting sort field to "No sort options available"');
				this.sortField = ['No sort options available'];
			}
		}

		this.dispatchEvent(new CustomEvent('search', {
			detail: {
			

				text: this.searchText,
				sortField: this.sortField,
				sortOrder: this.sortOrder,
				filters: this.filterField,
				view: this.currentMode,
			},
			bubbles: true,
			composed: true
		}));
	}

	debounce(func, delay) {
		clearTimeout(this.debounceTimer);
		this.debounceTimer = setTimeout(func, delay);
	}
}

customElements.define('studio-gallery-controls', StudioGalleryControlBar);
